import { Routes } from '@angular/router';
import {
  AuthRoutesEnum,
  CourtsRoutesEnum,
  EmployeeRoutesEnum,
  NotificationRoutesEnum,
  ProfileRoutesEnum,
  SchedulerRoutesEnum,
  CrudSportCenterEnum,
  OpeningHoursRoutesEnum,
  HomeRoutesEnum,
  SettingsPageEnum,
  ClassesRoutesEnum,
  AccountsPayableRoutesEnum,
} from '@libs/entity-lib';
import { authGuard } from '@libs/auth-lib';
import {
  AGENDA_LEGEND_HANDLER,
  BLOCK_RELEASE_HANDLER,
  DAY_USE_HANDLER,
  COURT_HEADER_HANDLER,
  EVENT_TYPE_PICKER_HANDLER,
  QUICK_CREATE_HANDLER,
} from '@libs/util-services';
import { AgendaLegendService } from './core/components/agenda-legend/agenda-legend.service';
import { LessorBlockReleaseHandler } from './core/services/block-period/block-release-handler.service';
import { LessorDayUseHandler } from './core/services/day-use/day-use-handler.service';
import { EventTypePickerService } from './core/components/event-type-picker/event-type-picker.service';
import { QuickCreateService } from './core/components/quick-create/quick-create.service';
import { LessorCourtHeaderHandler } from './core/services/court/court-header.service';
import { moduleGuard } from './guards/module.guard';
import { moduleMatchGuard } from './guards/module-match.guard';
import { updateLanguageRoutes } from '@libs/translation';
import {
  fixedTimeRoutes,
  financialDashboardRoutes,
  chargesRoutes,
  chatRoutes,
  dashboardRoutes,
} from '@libs/util-ui';
import { clientRoutes } from '@libs/client';
import { maintenanceRoutes } from '@libs/maintenance';
import { commonEnvironment } from '@libs/shared/util-environments';
import { billingAddressRoutes } from '@libs/billing-address';
import { notificationsRoutes } from '@libs/notifications';
import { termsRoutes, pendingTermsGuard } from '@libs/terms';

export const appRoutes: Routes = [
  {
    path: '',
    redirectTo: HomeRoutesEnum.HOME,
    pathMatch: 'full',
  },
  {
    path: 'seja-o-nosso-parceiro',
    loadChildren: () =>
      import('./pages/walkthrough/walkthrough-routes').then((m) => m.walkthroughRoutes),
  },
  {
    path: HomeRoutesEnum.HOME,
    loadChildren: () => import('./home/home-routes').then((m) => m.homeRoutes),
    canActivate: [pendingTermsGuard('terms')],
  },
  {
    path: AuthRoutesEnum.AUTH,
    loadChildren: () => import('./auth/auth-routes').then((m) => m.authRoutes),
  },
  {
    path: ProfileRoutesEnum.PROFILE,
    loadChildren: () => import('./pages/profile/profile-routes').then((m) => m.profileRoutes),
    canActivate: [authGuard],
  },
  {
    // Perfil da PESSOA (nome e foto). Separado de /profile, que é o centro
    // esportivo — a tela antiga misturava os dois e o sócio não tinha o dele.
    path: ProfileRoutesEnum.MY_PROFILE,
    loadComponent: () => import('./pages/my-profile/my-profile.page').then((m) => m.MyProfilePage),
    canActivate: [authGuard],
  },
  {
    path: OpeningHoursRoutesEnum.OPENING_HOURS,
    loadComponent: () =>
      import('./pages/opening-hours/opening-hours.page').then((m) => m.OpeningHoursPage),
    canActivate: [authGuard],
  },
  {
    path: CourtsRoutesEnum.COURTS,
    loadChildren: () => import('./pages/court/court-routes').then((m) => m.courtRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'fixed-time',
    loadChildren: () => fixedTimeRoutes,
  },
  // A raiz de /aulas é o HUB (só no lessor). O `pathMatch: 'full'` deixa todo o
  // resto cair no classesRoutes da lib logo abaixo — que é compartilhado com o
  // app athlete e por isso não pode conhecer o hub do CE.
  {
    path: ClassesRoutesEnum.CLASSES,
    pathMatch: 'full',
    loadComponent: () => import('./pages/aulas/aulas.page').then((m) => m.AulasPage),
    canActivate: [moduleGuard('AULAS')],
  },
  {
    path: `${ClassesRoutesEnum.CLASSES}/termo-saude/pendentes`,
    loadComponent: () =>
      import('./pages/aulas/health-term/health-term-pending.page').then(
        (m) => m.HealthTermPendingPage
      ),
    canActivate: [moduleGuard('AULAS')],
  },
  {
    path: `${ClassesRoutesEnum.CLASSES}/termo-saude`,
    loadComponent: () =>
      import('./pages/aulas/health-term/health-term.page').then((m) => m.HealthTermPage),
    canActivate: [moduleGuard('AULAS')],
  },
  {
    path: ClassesRoutesEnum.CLASSES,
    loadChildren: () => import('@libs/class-management').then((m) => m.classesRoutes),
    canActivate: [moduleGuard('AULAS')],
  },
  {
    path: CrudSportCenterEnum.CRUD,
    loadChildren: () => import('./pages/crud/crud-routes').then((m) => m.crudRoutes),
  },
  {
    path: SchedulerRoutesEnum.SCHEDULER,
    loadChildren: () => import('./pages/scheduler/scheduler-routes').then((m) => m.schedulerRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'qr-code',
    loadComponent: () => import('./pages/qr-code/qr-code.page').then((m) => m.QrCodePage),
    canActivate: [authGuard],
  },
  {
    // Assinaturas do CE (termos assinados por qualquer pessoa neste centro).
    path: 'signatures',
    loadComponent: () => import('./pages/signatures/signatures.page').then((m) => m.SignaturesPage),
    canActivate: [authGuard],
  },
  {
    path: 'visitor-guide',
    loadChildren: () =>
      import('./pages/visitor-guide/visitor-guide-routes').then((m) => m.visitorGuideRoutes),
    canActivate: [authGuard],
  },
  {
    path: EmployeeRoutesEnum.EMPLOYEE,
    loadChildren: () => import('@libs/employee-lib').then((m) => m.employeeRoutes),
    canActivate: [authGuard],
  },
  {
    path: NotificationRoutesEnum.NOTIFICATIONS,
    loadChildren: () => notificationsRoutes,
    canActivate: [authGuard],
  },
  {
    path: SettingsPageEnum.SETTINGS,
    loadChildren: () => import('./pages/settings/settings-routing').then((m) => m.settingsRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'contas-recebimento',
    loadComponent: () =>
      import('./pages/receiver-accounts/receiver-accounts-hub.component').then(
        (m) => m.ReceiverAccountsHubComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'equipments',
    loadChildren: () =>
      import('./pages/equipments/equipments.routes').then((m) => m.equipmentsRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'pacotes',
    loadChildren: () => import('./pages/packages/packages.routes').then((m) => m.packagesRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'customers',
    loadChildren: () => import('./pages/customers/customers-routes').then((m) => m.customersRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'financial-dashboard',
    loadChildren: () => financialDashboardRoutes,
  },
  {
    path: 'charges',
    loadChildren: () => chargesRoutes,
  },
  {
    path: 'dashboard',
    loadChildren: () => dashboardRoutes,
  },
  {
    path: 'chat',
    loadChildren: () => chatRoutes,
  },
  {
    path: 'shop',
    loadChildren: () => import('@libs/shop').then((m) => m.shopRoutes),
    canActivate: [() => commonEnvironment.featuresFlags.shop],
  },
  {
    path: 'produtos',
    loadChildren: () => import('@libs/shop').then((m) => m.shopRoutes),
    data: { productCategory: 'PRODUTO' },
    canActivate: [moduleGuard('PRODUTOS')],
  },
  {
    path: 'ingressos',
    loadChildren: () => import('@libs/ticket').then((m) => m.ticketLessorRoutes),
    canMatch: [moduleMatchGuard('INGRESSOS')],
  },
  {
    path: 'treinos',
    loadChildren: () => import('@libs/training').then((m) => m.trainingLessorRoutes),
    canMatch: [moduleMatchGuard('TREINOS')],
  },
  {
    path: 'matriculas',
    loadChildren: () => import('@libs/membership').then((m) => m.membershipLessorRoutes),
  },
  {
    path: 'privacy',
    loadComponent: () => import('./pages/privacy/privacy.page').then((m) => m.PrivacyPage),
  },
  {
    path: 'terms',
    children: termsRoutes,
  },
  {
    path: 'timezone',
    loadChildren: () => import('./pages/timezone/timezone-routes').then((m) => m.timezoneRoutes),
  },
  {
    path: 'language',
    children: updateLanguageRoutes,
  },
  {
    path: 'clients',
    children: clientRoutes,
  },
  {
    path: 'restaurant',
    canActivate: [() => commonEnvironment.featuresFlags.restaurant, moduleGuard('RESTAURANTE')],
    loadChildren: () => import('@libs/restaurant').then((m) => m.restaurantRoutes),
  },
  {
    path: 'championship',
    canActivate: [() => commonEnvironment.featuresFlags.tournament],
    loadChildren: () => import('@libs/tournament').then((m) => m.tournamentRoutes),
  },
  {
    path: 'add-athlete',
    loadComponent: () =>
      import('./pages/add-athlete/add-athlete.page').then((m) => m.AddAthletePage),
  },
  {
    path: 'step-by-step',
    loadChildren: () =>
      import('./pages/step-by-step/step-by-step-routes').then((m) => m.StepByStepRoutes),
  },
  {
    path: 'transfer-booking',
    loadChildren: () => import('@libs/transfer-booking').then((m) => m.TransferBookingRoutes),
  },
  {
    path: 'bloquear-periodo',
    loadChildren: () =>
      import('./pages/block-period/block-periodo.routes').then((m) => m.blockPeriodRoutes),
  },
  {
    path: 'maintenance',
    loadChildren: () => maintenanceRoutes,
  },
  {
    path: 'agendamentos',
    loadChildren: () =>
      import('./pages/agendamentos/agendamentos-routes').then((m) => m.agendamentosRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'no-permission',
    loadComponent: () =>
      import('./shared/empty-states/no-permission-page/no-permission.page').then(
        (m) => m.NoPermissionPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'subscription',
    loadChildren: () =>
      import('./pages/subscription/subscription-routes').then((m) => m.subscriptionRoutes),
    // A welcome tinha estado próprio de deslogado; o hub é autenticado-only —
    // o guard cobre deep-links de quem caía na /welcome sem sessão.
    canActivate: [authGuard],
  },
  {
    // "Montar seu plano" foi absorvida pelo hub /subscription (modo setup:
    // monta plano novo ou reativa o anterior). Redirect preserva deep-links.
    path: 'welcome',
    redirectTo: '/subscription',
    pathMatch: 'prefix',
  },
  {
    // Loja de módulos foi consolidada em /subscription como tab "Loja".
    // Redirect preserva deep-links antigos.
    path: 'modules',
    redirectTo: '/subscription?tab=store',
    pathMatch: 'full',
  },
  {
    // Detalhe de módulo mantido para deep-link (notificações push, marketing).
    path: 'module',
    loadChildren: () =>
      import('./pages/module-detail/module-detail-routes').then((m) => m.moduleDetailRoutes),
    canActivate: [authGuard],
  },
  {
    // Welcome legada (só redirecionava) — aposentada junto com a /welcome.
    path: 'vip-access',
    redirectTo: '/subscription',
    pathMatch: 'full',
  },
  {
    path: 'cliente/:athleteId',
    loadChildren: () =>
      import('./pages/client-update/client.routes').then((m) => m.clientUpdateRoutes),
  },
  {
    path: 'multi-contas',
    loadChildren: () => import('./pages/accounts/accounts-routes').then((m) => m.accountsRoutes),
  },
  {
    path: 'my-cards',
    loadChildren: () => import('./pages/my-cards/my-cards-routes').then((m) => m.myCardsRoutes),
  },
  {
    path: 'billing-address',
    loadChildren: () => billingAddressRoutes,
  },
  {
    path: 'nfc',
    loadChildren: () => import('@libs/nfc').then((m) => m.nfcRoutes),
  },
  {
    path: 'accommodation',
    loadChildren: () => import('@libs/accommodation').then((m) => m.accommodationRoutes),
    canMatch: [moduleMatchGuard('HOSPEDAGEM')],
  },
  {
    path: 'dayuse',
    loadChildren: () => import('@libs/day-use').then((m) => m.dayUseRoutes),
    canMatch: [moduleMatchGuard('DAY_USE')],
  },
  {
    path: 'single-payment',
    loadChildren: () => import('@libs/single-payment').then((m) => m.singlePaymentRoutes),
  },
  {
    path: 'financeiro',
    loadComponent: () => import('./pages/financeiro/financeiro.page').then((m) => m.FinanceiroPage),
    canActivate: [authGuard],
  },
  {
    path: 'integracoes',
    loadComponent: () =>
      import('./pages/integracoes/integracoes.page').then((m) => m.IntegracoesPage),
    canActivate: [moduleGuard('INTEGRACOES')],
  },
  {
    path: 'wellhub',
    loadChildren: () => import('@libs/class-management').then((m) => m.wellhubRoutes),
    canActivate: [moduleGuard('INTEGRACOES')],
  },
  {
    path: 'totalpass',
    loadChildren: () => import('@libs/totalpass').then((m) => m.totalPassRoutes),
    canActivate: [moduleGuard('INTEGRACOES')],
  },
  {
    path: AccountsPayableRoutesEnum.ACCOUNTS_PAYABLE,
    loadChildren: () => import('@libs/accounts-payable-lib').then((m) => m.accountsPayableRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'checkout',
    loadChildren: () => import('@libs/checkout').then((m) => m.checkoutRoutes),
  },
  {
    path: 'report',
    loadChildren: () => import('@libs/reports').then((m) => m.reportsRoutes),
  },
  {
    path: 'sport',
    loadChildren: () => import('@libs/sport').then((m) => m.sportRoutes),
  },
  {
    path: 'agenda',
    loadChildren: () => import('@libs/calendar').then((m) => m.calendarRoutes),
    providers: [
      { provide: BLOCK_RELEASE_HANDLER, useExisting: LessorBlockReleaseHandler },
      { provide: DAY_USE_HANDLER, useExisting: LessorDayUseHandler },
      { provide: EVENT_TYPE_PICKER_HANDLER, useExisting: EventTypePickerService },
      { provide: AGENDA_LEGEND_HANDLER, useExisting: AgendaLegendService },
      { provide: COURT_HEADER_HANDLER, useExisting: LessorCourtHeaderHandler },
      { provide: QUICK_CREATE_HANDLER, useExisting: QuickCreateService },
      // SPORT_CENTER_TIMEZONE_PROVIDER subiu pro root (app.config.ts) — a
      // agenda de aulas também precisa dele, não só esta rota.
    ],
  },
  {
    path: 'escalada',
    loadChildren: () => import('@libs/climbing').then((m) => m.climbingRoutes),
  },
  {
    path: 'trilhas',
    // O mapa EXPLORE abre região/setor na escalada; no lessor os segmentos são
    // `regiao/setores` (admin/atleta usam o default `regions/sectors`).
    loadChildren: () =>
      import('@libs/trails').then((m) => [
        {
          path: '',
          providers: [
            { provide: m.EXPLORE_CLIMBING_LINKS, useValue: m.LESSOR_EXPLORE_CLIMBING_LINKS },
          ],
          children: m.trailsRoutes,
        },
      ]),
    canMatch: [moduleMatchGuard('HIKING')],
  },
  {
    path: 'audit',
    loadChildren: () => import('@libs/audit').then((m) => m.auditRoutes),
    canActivate: [authGuard],
  },
  {
    path: '**',
    redirectTo: HomeRoutesEnum.HOME,
  }, // rota catch-all para redirecionar para a Home
];
